import Image from 'next/image';
import Link from 'next/link';
import { getReadableImageUrl } from "@/lib/directus";
import { sanitizeHTML } from "@/lib/utils";

interface TravellingSectionData {
  heading?: {
    text?: string;
    title?: string;
  };
  text?: {
    text?: string;
  };
  image?: {
    alt?: string;
    image_file?: {
      image?: {
        id?: string;
        filename_download?: string;
      };
    };
  };
  button?: {
    label?: string;
    href?: string;
  };
}

export const fields = [
  // heading
  "heading.text",
  "heading.title",
  
  // text
  "text.text",
  
  // image
  "image.alt",
  "image.image_file.image.id",
  "image.image_file.image.filename_download",
  
  // button
  "button.label",
  "button.href",
] as const;

interface TravellingSectionNonAnimatedProps {
  data?: TravellingSectionData;
}

export default function TravellingSectionNonAnimated({ data }: TravellingSectionNonAnimatedProps) {
  // Prepare image URL
  const imageId = data?.image?.image_file?.image?.id;
  const imageFilename = data?.image?.image_file?.image?.filename_download;
  const imageUrl = imageId && imageFilename 
    ? getReadableImageUrl(imageId, imageFilename)
    : "/images/AdobeStock_880656495_Preview.png";

  return (
    <section className="relative w-full py-8 lg:py-16 overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0 w-full">
        <Image
          src={imageUrl}
          alt={data?.image?.alt || "Travel background"}
          fill
          className="object-cover"
          sizes="100vw"
          quality={65}
        />
      </div>

      {/* Content Container */}
      <div className="relative container mx-auto px-5 md:px-0">
        <div className="w-full xl:w-[70%] mx-auto flex flex-col md:flex-row md:items-start md:gap-16">
          {/* Left Content Box */}
          <div className="w-full md:w-1/2 bg-white/50 backdrop-blur-lg rounded-[1.25rem] p-6 md:p-8 mb-7 md:mb-0 desktopFadeFromLeft">
            <h2 className="text-[2rem] font-essonnes font-light leading-tight md:text-5xl lg:text-[3.375rem] text-primary">
              {data?.heading?.title}
            </h2>
          </div>

          {/* Right Content */}
          <div className="w-full md:w-1/2 flex flex-col gap-8 self-center desktopFadeFromRight">
            <div 
              className="text-[1rem] font-normal leading-normal md:text-xl text-primary font-greycliff"
              dangerouslySetInnerHTML={sanitizeHTML(data?.text?.text || "")}
            />
            
            {data?.button?.href && data?.button?.label && (
              <Link 
                href={data?.button?.href || "#"}
                className="w-full md:w-auto bg-primary hover:bg-light hover:shadow-lg text-white hover:text-primary rounded-full py-3 px-12 text-xl font-greycliff leading-7 tracking-tight self-start transition-all duration-300 text-center">
                {data?.button?.label}
              </Link>
            )}
          </div>
        </div>
      </div>
    </section>
  );
} 